// Best-effort persistence of unsent composer text so a reply/note survives
// switching conversations and full page reloads (REQ-05). Drafts are keyed by
// Chatwoot conversation id and composer mode, stored in one localStorage entry.
// Everything is wrapped so storage being unavailable/full never breaks the
// composer — a failed read/write just means no persistence.

const STORAGE_KEY = 'chatwoot:composerDrafts'
// Cap stored conversations so the entry can't grow without bound; the oldest
// (by last update) are evicted first.
const MAX_ENTRIES = 50

export type ComposerMode = 'reply' | 'note'

interface DraftEntry {
  reply?: string
  note?: string
  updatedAt: number
}

type DraftMap = Record<string, DraftEntry>

function readMap(): DraftMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as DraftMap) : {}
  } catch {
    return {}
  }
}

function writeMap(map: DraftMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Storage unavailable or over quota — drafts are best-effort, so ignore.
  }
}

function prune(map: DraftMap): void {
  const ids = Object.keys(map)
  if (ids.length <= MAX_ENTRIES) {
    return
  }
  ids.sort((a, b) => map[a].updatedAt - map[b].updatedAt)
  for (const id of ids.slice(0, ids.length - MAX_ENTRIES)) {
    delete map[id]
  }
}

export function loadDraft(conversationId: string, mode: ComposerMode): string {
  return readMap()[conversationId]?.[mode] ?? ''
}

// Store (or, for empty text, remove) the draft for one conversation+mode.
// A conversation entry with neither a reply nor a note draft is dropped.
export function saveDraft(
  conversationId: string,
  mode: ComposerMode,
  value: string
): void {
  const map = readMap()
  const entry: DraftEntry = map[conversationId] ?? { updatedAt: 0 }

  if (value) {
    entry[mode] = value
  } else {
    delete entry[mode]
  }
  entry.updatedAt = Date.now()

  if (!entry.reply && !entry.note) {
    delete map[conversationId]
  } else {
    map[conversationId] = entry
    prune(map)
  }

  writeMap(map)
}

export function clearDraft(conversationId: string, mode: ComposerMode): void {
  saveDraft(conversationId, mode, '')
}

// The new-conversation modal has no conversation to key on, so closing it — or
// a failed send followed by a reload — would otherwise throw away the whole
// email. Its own entry keeps that composer's subject and body across remounts,
// and is cleared only once the conversation is created. Attachments for the
// same composer live in composerOutbox.ts.
const NEW_CONVERSATION_KEY = 'chatwoot:newConversationDraft'

export interface NewConversationDraft {
  subject: string
  content: string
  cc: string[]
  bcc: string[]
}

const emptyNewConversationDraft: NewConversationDraft = {
  subject: '',
  content: '',
  cc: [],
  bcc: [],
}

function readAddresses(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

export function loadNewConversationDraft(): NewConversationDraft {
  try {
    const raw = localStorage.getItem(NEW_CONVERSATION_KEY)
    if (!raw) {
      return emptyNewConversationDraft
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return emptyNewConversationDraft
    }
    const { subject, content, cc, bcc } =
      parsed as Partial<NewConversationDraft>
    return {
      subject: typeof subject === 'string' ? subject : '',
      content: typeof content === 'string' ? content : '',
      cc: readAddresses(cc),
      bcc: readAddresses(bcc),
    }
  } catch {
    return emptyNewConversationDraft
  }
}

export function saveNewConversationDraft(draft: NewConversationDraft): void {
  try {
    if (
      !draft.subject &&
      !draft.content &&
      draft.cc.length === 0 &&
      draft.bcc.length === 0
    ) {
      localStorage.removeItem(NEW_CONVERSATION_KEY)
      return
    }
    localStorage.setItem(NEW_CONVERSATION_KEY, JSON.stringify(draft))
  } catch {
    // Storage unavailable or over quota — drafts are best-effort, so ignore.
  }
}

export function clearNewConversationDraft(): void {
  try {
    localStorage.removeItem(NEW_CONVERSATION_KEY)
  } catch {
    // As above.
  }
}
